/* eslint-disable react/prop-types */
// Mini-mockup of each element, keyed by AtomicBox type or plugin name; falls back
// to the plugin's Material icon when no mockup exists. Smart-element text reuses
// the real elements' i18n sample keys so previews stay localized and accurate.
import React from "react";
import PropTypes from "prop-types";
import i18n from "i18next";

const t = (key, opts) => i18n.t(key, opts);

const C = {
  ink: "#1e293b",
  sub: "#64748b",
  faint: "#94a3b8",
  line: "#e2e8f0",
  lineSoft: "#eef2f7",
  brand: "#7f56d9",
  brandSoft: "#ede9fe",
  brandTint: "#f4f0fe",
  surface: "#ffffff",
};

const Bar = ({ w = "100%", h = 5, c = C.line, r = 3, style }) => (
  <div style={{ width: w, height: h, background: c, borderRadius: r, flexShrink: 0, ...style }} />
);
const Txt = ({ children, size = 8, weight = 600, c = C.ink, style }) => (
  <div
    style={{
      fontSize: size,
      fontWeight: weight,
      color: c,
      lineHeight: 1.2,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      ...style,
    }}
  >
    {children}
  </div>
);
const col = { display: "flex", flexDirection: "column" };
const row = { display: "flex", flexDirection: "row" };
const center = { alignItems: "center", justifyContent: "center" };

const card = {
  ...col,
  gap: 3,
  padding: 5,
  background: C.surface,
  border: `1px solid ${C.line}`,
  borderRadius: 5,
};

// Image / video placeholders that match the real dropped box (gray fill + icon),
// shared by the card mockup and the drag ghost so they look identical.
const MEDIA_BG = "#cfcfcf";
const MEDIA_ICON = "#5a5a5a";

export const ImagePlaceholder = () => (
  <div style={{ width: "100%", height: "100%", background: MEDIA_BG, ...row, ...center }}>
    <svg viewBox="0 0 60 52" width="42%" style={{ maxWidth: 128, minWidth: 46 }} fill="none">
      <rect x="3.5" y="3.5" width="53" height="45" rx="5" stroke={MEDIA_ICON} strokeWidth="4" />
      <circle cx="19" cy="19" r="5" fill={MEDIA_ICON} />
      <path d="M8 46 L24 27 L33 36 L43 24 L53 46 Z" fill={MEDIA_ICON} />
    </svg>
  </div>
);

export const VideoPlaceholder = () => (
  <div
    style={{
      width: "100%",
      height: "100%",
      background: MEDIA_BG,
      position: "relative",
      ...row,
      ...center,
    }}
  >
    <svg
      viewBox="0 0 72 42"
      width="44%"
      style={{ maxWidth: 150, minWidth: 56, marginBottom: "12%" }}
      fill="none"
    >
      <rect x="3" y="5" width="66" height="32" rx="5" stroke={MEDIA_ICON} strokeWidth="3.5" />
      {[11, 22, 33, 44, 55].map((x) => (
        <React.Fragment key={x}>
          <rect x={x} y="8.5" width="6.5" height="4" rx="1" fill={MEDIA_ICON} />
          <rect x={x} y="29.5" width="6.5" height="4" rx="1" fill={MEDIA_ICON} />
        </React.Fragment>
      ))}
      <path d="M30 15 L44 21 L30 27 Z" fill={MEDIA_ICON} />
    </svg>
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: "20%",
        minHeight: 16,
        background: "#585858",
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "0 7px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: 0,
          height: 0,
          borderTop: "4px solid transparent",
          borderBottom: "4px solid transparent",
          borderLeft: "7px solid #fff",
          flexShrink: 0,
        }}
      />
      <div
        style={{
          flex: 1,
          height: 3,
          background: "rgba(255,255,255,0.5)",
          borderRadius: 2,
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: "62%",
            top: "50%",
            transform: "translate(-50%,-50%)",
            width: 8,
            height: 8,
            borderRadius: 8,
            background: "#5ec8d0",
          }}
        />
      </div>
      <span style={{ color: "#fff", fontSize: 7, whiteSpace: "nowrap" }}>0:00/0:05</span>
      <div style={{ position: "relative", width: 9, height: 9, flexShrink: 0 }}>
        {[
          { top: 0, left: 0, bt: 1 },
          { top: 0, right: 0, bt: 1 },
          { bottom: 0, left: 0, bb: 1 },
          { bottom: 0, right: 0, bb: 1 },
        ].map((p, i) => (
          <span
            key={i}
            style={{
              position: "absolute",
              top: p.top,
              bottom: p.bottom,
              left: p.left,
              right: p.right,
              width: 3,
              height: 3,
              borderTop: p.bt ? "1.5px solid #fff" : undefined,
              borderBottom: p.bb ? "1.5px solid #fff" : undefined,
              borderLeft: p.left === 0 ? "1.5px solid #fff" : undefined,
              borderRight: p.right === 0 ? "1.5px solid #fff" : undefined,
            }}
          />
        ))}
      </div>
    </div>
  </div>
);

// Faithful table matching the real DataTable box (header + data rows).
export const TablePreview = () => {
  const cell = { padding: "3px 5px", borderRight: "1px solid #111", boxSizing: "border-box" };
  const rows = [
    ["Ronald Armstrong", "31", "Malta"],
    ["Mary Smith", "23", "Canada"],
    ["Marion Gilbert", "18", "Australia"],
    ["John Doe", "16", "USA"],
  ];
  return (
    <div
      style={{
        width: "100%",
        background: "#fff",
        border: "1px solid #111",
        borderBottom: "none",
        fontSize: 6.5,
        fontFamily: "Arial, sans-serif",
        color: "#111",
        lineHeight: 1.25,
      }}
    >
      <div
        style={{
          display: "flex",
          background: "#d1d5db",
          fontWeight: 700,
          borderBottom: "1px solid #111",
        }}
      >
        <div style={{ ...cell, flex: 2 }}>Name Surname</div>
        <div style={{ ...cell, flex: 1 }}>Age</div>
        <div style={{ ...cell, flex: 1, borderRight: "none" }}>Country</div>
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "flex", borderBottom: "1px solid #111" }}>
          <div style={{ ...cell, flex: 2 }}>{r[0]}</div>
          <div style={{ ...cell, flex: 1 }}>{r[1]}</div>
          <div style={{ ...cell, flex: 1, borderRight: "none" }}>{r[2]}</div>
        </div>
      ))}
    </div>
  );
};

// Faithful chart matching GraficaD3 — bars + an area overlay so it reads as a
// chart that supports multiple types (bar / area).
// Chart — bars + area drawn in ONE SVG (single coordinate system so they align,
// no shift). viewBox gives an intrinsic aspect so it sizes cleanly anywhere.
export const ChartPreview = () => (
  <svg viewBox="0 0 124 78" width="100%" style={{ display: "block" }}>
    <polygon points="8,68 116,22 116,68" fill="#ddd6fe" opacity="0.7" />
    <polyline points="8,68 116,22" stroke="#7c6df2" strokeWidth="2.5" fill="none" />
    <rect x="12" y="44" width="15" height="24" rx="2.5" fill="#c4b5fd" />
    <rect x="35" y="30" width="15" height="38" rx="2.5" fill="#7c3aed" />
    <rect x="58" y="40" width="15" height="28" rx="2.5" fill="#c4b5fd" />
    <rect x="81" y="24" width="15" height="44" rx="2.5" fill="#7c3aed" />
    <rect x="104" y="36" width="15" height="32" rx="2.5" fill="#c4b5fd" />
  </svg>
);

// ShapeOverlay = a colored overlay that highlights / draws attention to content
// (the default is a semi-transparent highlight; color + shape are editable). No
// white box — it reads as an overlay over the canvas during drag (like AudioCue).
export const ShapePreview = () => (
  <div style={{ width: "100%", padding: "8px 6px", boxSizing: "border-box" }}>
    <div
      style={{
        width: "100%",
        height: 44,
        background: "rgba(239,68,68,0.32)",
        border: "1.5px solid rgba(239,68,68,0.7)",
        borderRadius: 6,
      }}
    />
  </div>
);

const MOCKUPS = {
  // Smart elements
  heading: () => (
    <Txt size={16} weight={800} style={{ whiteSpace: "normal" }}>
      {t("SE_sample_heading")}
    </Txt>
  ),
  eyebrow: () => (
    <Txt
      size={9}
      weight={700}
      c={C.brand}
      style={{ letterSpacing: "0.08em", textTransform: "uppercase", whiteSpace: "normal" }}
    >
      {t("SE_sample_eyebrow")}
    </Txt>
  ),
  footer: () => (
    <div style={{ ...col, gap: 5, justifyContent: "flex-end", height: "100%" }}>
      <Bar w="100%" h={1} c={C.line} />
      <Txt size={8} weight={500} c={C.sub} style={{ whiteSpace: "normal" }}>
        {t("SE_sample_footer")}
      </Txt>
    </div>
  ),
  quote: () => (
    <div style={{ ...row, gap: 6, alignItems: "flex-start" }}>
      <div style={{ fontSize: 24, lineHeight: "16px", color: C.brand, fontWeight: 800 }}>
        &ldquo;
      </div>
      <div style={{ ...col, gap: 4, flex: 1 }}>
        <Txt size={9} weight={600} c={C.ink} style={{ whiteSpace: "normal", fontStyle: "italic" }}>
          {t("SE_sample_quote_text")}
        </Txt>
        <Txt size={7} weight={600} c={C.sub}>
          — {t("SE_sample_quote_author")}
        </Txt>
      </div>
    </div>
  ),
  "bullet-list": () => (
    <div style={{ ...col, gap: 6 }}>
      <Txt size={9} weight={700}>
        {t("SE_sample_bullet_title")}
      </Txt>
      {[1, 2, 3].map((n) => (
        <div key={n} style={{ ...row, alignItems: "center", gap: 6 }}>
          <Bar w={5} h={5} c={C.brand} r={5} />
          <Txt size={8} weight={500} c={C.ink}>
            {t("SE_sample_bullet_point", { n })}
          </Txt>
        </div>
      ))}
    </div>
  ),
  "card-grid": () => (
    <div style={{ ...col, gap: 5 }}>
      <Txt size={9} weight={700}>
        {t("SE_sample_card_title")}
      </Txt>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
        {[1, 2].map((n) => (
          <div key={n} style={card}>
            <Txt size={6} weight={700} c={C.brand}>
              {t("SE_sample_card_item_title", { n })}
            </Txt>
            <Txt size={5} weight={500} c={C.sub}>
              {t("SE_sample_card_item_body", { n })}
            </Txt>
          </div>
        ))}
      </div>
    </div>
  ),
  // Real timeline = title + a thin progress track + step cards (not bare dots).
  timeline: () => (
    <div style={{ ...col, gap: 6, width: "100%" }}>
      <Txt size={9} weight={700}>
        {t("SE_sample_timeline_title")}
      </Txt>
      <Bar w="100%" h={3} c={C.brand} r={3} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
        {[1, 2].map((n) => (
          <div key={n} style={card}>
            <Txt size={6} weight={700} c={C.brand}>
              {t("SE_sample_timeline_step", { n })}
            </Txt>
            <Bar w="100%" h={3} c={C.lineSoft} />
          </div>
        ))}
      </div>
    </div>
  ),
  "stat-group": () => (
    <div style={{ ...row, justifyContent: "space-around", gap: 8 }}>
      {[
        ["100", 1],
        ["50%", 2],
      ].map(([v, n]) => (
        <div key={n} style={{ ...col, ...center, gap: 2, flex: 1 }}>
          <Txt size={16} weight={800} c={C.brand} style={{ textAlign: "center" }}>
            {v}
          </Txt>
          <Txt size={6} weight={500} c={C.sub} style={{ textAlign: "center" }}>
            {t("SE_sample_stat_label", { n })}
          </Txt>
        </div>
      ))}
    </div>
  ),
  hero: () => (
    <div style={{ ...col, gap: 5, padding: 9, background: C.brandTint, borderRadius: 6 }}>
      <Txt size={14} weight={800}>
        {t("SE_sample_hero_heading")}
      </Txt>
      <Txt size={8} weight={500} c={C.sub} style={{ whiteSpace: "normal" }}>
        {t("SE_sample_hero_body")}
      </Txt>
    </div>
  ),
  section: () => (
    <div style={{ ...col, gap: 4 }}>
      <Txt size={10} weight={800} c={C.brand}>
        01
      </Txt>
      <Txt size={12} weight={800}>
        {t("SE_sample_section_heading")}
      </Txt>
      <Txt size={7} weight={500} c={C.sub} style={{ whiteSpace: "normal" }}>
        {t("SE_sample_section_body")}
      </Txt>
    </div>
  ),
  "two-column": () => (
    <div style={{ ...row, gap: 8 }}>
      {["SE_sample_2col_left_title", "SE_sample_2col_right_title"].map((key) => (
        <div key={key} style={{ ...col, gap: 3, flex: 1 }}>
          <Txt size={7} weight={700} c={C.brand}>
            {t(key)}
          </Txt>
          <Txt size={6} weight={500} c={C.sub} style={{ whiteSpace: "normal" }}>
            {t("SE_sample_2col_body")}
          </Txt>
        </div>
      ))}
    </div>
  ),
  comparison: () => (
    <div style={{ ...row, gap: 6 }}>
      {["A", "B"].map((letter) => (
        <div key={letter} style={{ ...card, flex: 1, gap: 4 }}>
          <Txt size={7} weight={700} c={C.brand}>
            {t("SE_sample_comp_option", { letter })}
          </Txt>
          {[1, 2].map((n) => (
            <div key={n} style={{ ...row, alignItems: "center", gap: 4 }}>
              <Bar w={3} h={3} c={C.brand} r={3} />
              <Txt size={5} weight={500} c={C.sub}>
                {t("SE_sample_comp_feature", { n })}
              </Txt>
            </div>
          ))}
        </div>
      ))}
    </div>
  ),
  divider: () => (
    <div style={{ ...row, ...center, width: "100%" }}>
      <Bar w="100%" h={2} c={C.line} />
    </div>
  ),
  badge: () => (
    <div style={{ ...row, ...center }}>
      <div
        style={{
          ...row,
          ...center,
          padding: "4px 12px",
          background: C.brandSoft,
          borderRadius: 999,
        }}
      >
        <Txt size={8} weight={700} c={C.brand}>
          {t("SE_sample_badge")}
        </Txt>
      </div>
    </div>
  ),
  "cta-button": () => (
    <div style={{ ...row, ...center }}>
      <div style={{ ...row, ...center, padding: "8px 18px", background: C.brand, borderRadius: 6 }}>
        <Txt size={9} weight={700} c={C.surface}>
          {t("SE_sample_cta")}
        </Txt>
      </div>
    </div>
  ),
  "icon-block": () => (
    <div style={{ ...row, gap: 7, alignItems: "center" }}>
      <div
        style={{
          ...row,
          ...center,
          width: 26,
          height: 26,
          background: C.brandSoft,
          borderRadius: 6,
          flexShrink: 0,
        }}
      >
        <span className="material-icons" style={{ fontSize: 16, color: C.brand }}>
          star
        </span>
      </div>
      <div style={{ ...col, gap: 3, flex: 1 }}>
        <Txt size={8} weight={700}>
          {t("SE_sample_icon_title")}
        </Txt>
        <Txt size={6} weight={500} c={C.sub}>
          {t("SE_sample_icon_body")}
        </Txt>
      </div>
    </div>
  ),

  // Basic / data / multimedia plugins — language-neutral visuals (no text).
  BasicText: () => (
    <div style={{ ...col, gap: 4, width: "100%" }}>
      <Bar w="60%" h={6} c={C.ink} />
      <Bar w="100%" h={3} c={C.lineSoft} />
      <Bar w="92%" h={3} c={C.lineSoft} />
      <Bar w="98%" h={3} c={C.lineSoft} />
      <Bar w="55%" h={3} c={C.lineSoft} />
    </div>
  ),

  // Clean framed symbols for the panel card (the real-looking placeholders are
  // shown only during the canvas drag — see RealElementPreview). Height-capped so
  // they sit centered with padding (not touching the card's top/bottom border).
  HotspotImages: () => (
    <svg
      viewBox="0 0 120 88"
      style={{ display: "block", height: 54, width: "auto", maxWidth: "100%", margin: "0 auto" }}
    >
      <rect
        x="1.5"
        y="1.5"
        width="117"
        height="85"
        rx="7"
        fill="#f8fafc"
        stroke="#e2e8f0"
        strokeWidth="2.5"
      />
      <circle cx="86" cy="27" r="9" fill="#c7d2fe" />
      <path d="M14 80 L44 46 L66 68 L86 49 L106 80 Z" fill="#cbd5e1" />
    </svg>
  ),
  EnrichedPlayer: () => (
    <svg
      viewBox="0 0 120 88"
      style={{ display: "block", height: 54, width: "auto", maxWidth: "100%", margin: "0 auto" }}
    >
      <rect
        x="1.5"
        y="1.5"
        width="117"
        height="85"
        rx="7"
        fill="#f8fafc"
        stroke="#e2e8f0"
        strokeWidth="2.5"
      />
      <circle cx="60" cy="44" r="17" fill="#ede9fe" stroke="#7f56d9" strokeWidth="2.5" />
      <path d="M55 36 L70 44 L55 52 Z" fill="#7f56d9" />
    </svg>
  ),
  EnrichedPDF: () => (
    <div style={{ ...row, ...center, width: "100%", height: "100%" }}>
      <div
        style={{
          position: "relative",
          width: 46,
          height: 56,
          background: C.surface,
          border: `1px solid ${C.line}`,
          borderRadius: 4,
          padding: 7,
          boxSizing: "border-box",
          ...col,
          gap: 4,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -1,
            right: -1,
            width: 14,
            height: 14,
            background: C.lineSoft,
            borderLeft: `1px solid ${C.line}`,
            borderBottom: `1px solid ${C.line}`,
            borderRadius: "0 0 0 4px",
          }}
        />
        <div
          style={{
            ...row,
            ...center,
            padding: "1px 4px",
            background: "#ef4444",
            borderRadius: 2,
            alignSelf: "flex-start",
          }}
        >
          <span style={{ fontSize: 5, fontWeight: 800, color: "#fff" }}>PDF</span>
        </div>
        <Bar w="100%" h={3} c={C.lineSoft} />
        <Bar w="100%" h={3} c={C.lineSoft} />
        <Bar w="60%" h={3} c={C.lineSoft} />
      </div>
    </div>
  ),
  ShapeOverlay: () => <ShapePreview />,
  // Card + drag both show this simple grid (the drag scales this same preview).
  DataTable: () => (
    <div
      style={{
        ...col,
        width: "100%",
        border: `1px solid ${C.line}`,
        borderRadius: 4,
        overflow: "hidden",
      }}
    >
      {[0, 1, 2].map((r) => (
        <div key={r} style={{ ...row }}>
          {[0, 1, 2].map((c) => (
            <div
              key={c}
              style={{
                flex: 1,
                height: 14,
                display: "flex",
                alignItems: "center",
                paddingLeft: 4,
                borderRight: c < 2 ? `1px solid ${C.lineSoft}` : "none",
                borderBottom: r < 2 ? `1px solid ${C.lineSoft}` : "none",
                background: r === 0 ? C.brandSoft : C.surface,
              }}
            >
              <Bar w="60%" h={3} c={r === 0 ? C.brand : C.line} />
            </div>
          ))}
        </div>
      ))}
    </div>
  ),
  GraficaD3: () => <ChartPreview />,
  AudioCue: () => (
    <div style={{ ...row, ...center, gap: 6, width: "100%" }}>
      <div
        style={{
          ...row,
          ...center,
          width: 22,
          height: 22,
          borderRadius: 22,
          background: C.brand,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 0,
            height: 0,
            borderTop: "5px solid transparent",
            borderBottom: "5px solid transparent",
            borderLeft: "8px solid #fff",
            marginLeft: 2,
          }}
        />
      </div>
      <div style={{ ...row, alignItems: "center", gap: 2, flex: 1, height: 24 }}>
        {[40, 80, 55, 100, 60, 85, 45, 70, 50].map((h, i) => (
          <Bar key={i} w={3} h={`${h}%`} c={i % 3 === 0 ? C.brand : "#cbd5e1"} r={2} />
        ))}
      </div>
    </div>
  ),
  OneClick: () => (
    <div style={{ ...row, ...center, width: "100%", height: "100%", position: "relative" }}>
      <div
        style={{
          position: "absolute",
          width: 30,
          height: 30,
          borderRadius: 30,
          border: `2px solid ${C.brandSoft}`,
        }}
      />
      <div
        style={{
          position: "absolute",
          width: 18,
          height: 18,
          borderRadius: 18,
          border: `2px solid ${C.brand}`,
        }}
      />
      <span
        className="material-icons"
        style={{
          fontSize: 22,
          color: C.brand,
          position: "relative",
          marginLeft: 14,
          marginTop: 14,
        }}
      >
        touch_app
      </span>
    </div>
  ),
  // Screen-training multi-input = its three add-able items: click, input, tag.
  GuideMultiInput: () => (
    <div style={{ ...col, gap: 7, width: "100%" }}>
      <div style={{ ...row, alignItems: "center", gap: 6 }}>
        <span className="material-icons" style={{ fontSize: 14, color: C.brand }}>
          touch_app
        </span>
        <div style={{ flex: 1, height: 9, border: `1.5px dashed ${C.brand}`, borderRadius: 3 }} />
      </div>
      <div style={{ ...row, alignItems: "center", gap: 6 }}>
        <span className="material-icons" style={{ fontSize: 14, color: C.sub }}>
          edit
        </span>
        <div
          style={{
            flex: 1,
            height: 11,
            border: `1px solid ${C.line}`,
            borderRadius: 3,
            background: C.surface,
          }}
        />
      </div>
      <div style={{ ...row, alignItems: "center", gap: 6 }}>
        <span className="material-icons" style={{ fontSize: 14, color: C.sub }}>
          label
        </span>
        <div
          style={{
            ...row,
            ...center,
            padding: "2px 9px",
            background: C.brandSoft,
            borderRadius: 999,
          }}
        >
          <Bar w={18} h={4} c={C.brand} />
        </div>
      </div>
    </div>
  ),
  // One tag — the element shows a single tag when added.
  GuideTagDisplay: () => (
    <div style={{ ...row, ...center, width: "100%" }}>
      <div
        style={{
          ...row,
          ...center,
          gap: 5,
          padding: "4px 12px",
          background: C.brandSoft,
          borderRadius: 999,
        }}
      >
        <span className="material-icons" style={{ fontSize: 11, color: C.brand }}>
          label
        </span>
        <Bar w={22} h={4} c={C.brand} />
      </div>
    </div>
  ),
};

const ElementPreview = ({ kind, icon, fill }) => {
  const mockup = MOCKUPS[kind];
  if (mockup) {
    return (
      <div style={{ width: "100%", height: "100%", ...row, ...center }}>
        {fill ? mockup() : <div style={{ width: "86%", maxWidth: 138 }}>{mockup()}</div>}
      </div>
    );
  }
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        ...row,
        ...center,
        background: C.brandTint,
        borderRadius: 8,
      }}
    >
      <span className="material-icons" style={{ fontSize: 34, color: C.brand }}>
        {icon}
      </span>
    </div>
  );
};

ElementPreview.propTypes = {
  kind: PropTypes.string, // AtomicBox type or plugin name
  icon: PropTypes.string, // Material icon ligature for the fallback
  fill: PropTypes.bool, // render the mockup edge-to-edge (drag footprint)
};

export default ElementPreview;
